import { useEffect, useRef } from "react";

const HISTORY_KEY = "__isugarModal";
const LOCK_ATTR = "data-modal-lock-count";

// Contador global: com dois modais empilhados (ex.: galeria → confirmação de
// exclusão), fechar o de cima não pode destravar a rolagem do de baixo.
const readLockCount = () =>
  Number(document.body.getAttribute(LOCK_ATTR) || "0");

const lockBodyScroll = () => {
  const count = readLockCount();
  document.body.setAttribute(LOCK_ATTR, String(count + 1));
  if (count > 0) return;

  // `overflow: hidden` no body não segura o Safari do iOS — a página de trás
  // continua rolando junto com o modal. Prender o body com `position: fixed`
  // deslocado pelo scroll atual é o único jeito que funciona nos dois, e o
  // deslocamento evita o salto visual para o topo.
  const scrollY = window.scrollY;
  document.body.dataset.modalScrollY = String(scrollY);
  document.body.style.position = "fixed";
  document.body.style.top = `-${scrollY}px`;
  document.body.style.left = "0";
  document.body.style.right = "0";
  document.body.style.width = "100%";
};

const unlockBodyScroll = () => {
  const count = readLockCount();
  const next = Math.max(0, count - 1);
  if (next > 0) {
    document.body.setAttribute(LOCK_ATTR, String(next));
    return;
  }

  document.body.removeAttribute(LOCK_ATTR);
  const scrollY = Number(document.body.dataset.modalScrollY || "0");
  delete document.body.dataset.modalScrollY;
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.width = "";
  window.scrollTo(0, scrollY);
};

let modalIdSeq = 0;

/**
 * Comportamentos que todo modal/bottom sheet precisa ter no mobile e que
 * nenhum dos 29 modais do app tinha:
 *
 *  - trava a rolagem do fundo (sem isso, rolar dentro do modal arrasta a
 *    página de trás junto assim que o conteúdo acaba);
 *  - fecha no Escape (só o PrivatePhotoLightbox tratava);
 *  - fecha no botão Voltar do Android, em vez de sair da página inteira —
 *    que é o que os usuários esperam de uma folha aberta.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {{ closeOnEscape?: boolean, closeOnBack?: boolean, lockScroll?: boolean }} [options]
 */
export function useModal(isOpen, onClose, options = {}) {
  const {
    closeOnEscape = true,
    closeOnBack = true,
    lockScroll = true,
  } = options;

  // Guarda o callback em ref para o effect não reassinar (e, no caso do
  // history, empurrar uma entrada nova) a cada render do componente pai.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || !lockScroll) return undefined;
    lockBodyScroll();
    return unlockBodyScroll;
  }, [isOpen, lockScroll]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, closeOnEscape]);

  useEffect(() => {
    if (!isOpen || !closeOnBack) return undefined;

    modalIdSeq += 1;
    const id = modalIdSeq;
    window.history.pushState({ [HISTORY_KEY]: id }, "");

    let closedByBackButton = false;
    const handlePopState = () => {
      closedByBackButton = true;
      onCloseRef.current?.();
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      // Fechou pelo X/backdrop: a entrada que empurramos continua na pilha e
      // precisa sair, senão o Voltar seguinte vira um no-op para o usuário.
      // A checagem do state evita desfazer uma navegação de rota: se o modal
      // sumiu porque a página mudou, a entrada já não é mais a nossa.
      if (
        !closedByBackButton &&
        window.history.state?.[HISTORY_KEY] === id
      ) {
        window.history.back();
      }
    };
  }, [isOpen, closeOnBack]);
}

export default useModal;
